import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Alert, Box, CircularProgress} from '@mui/material';
import * as api from '../api/otherTransactionsFunctions';
import {getOtherTransactionsStages} from '../constants/otherTransactionsStages';
import {toUserFacingError} from '../utils/userFacingError';
import StageWrapper from './StageWrapper';
import OtherTransactionsWorkspace from './OtherTransactionsWorkspace';


type SharedShellProps = Pick<React.ComponentProps<typeof StageWrapper>,
  'readOnlyReason' | 'stageRatings' | 'onStageRatingChange' | 'onStageThumbsDown' | 'isSampleFlow' | 'onSampleContinue'>;

export type OtherTransactionsDisplayStage = {name: string; description: string};

interface Props {
  flowId: string;
  workflowName?: string;
  currentStage: number;
  onStageChange: (index: number) => void;
  onBack: () => void;
  readOnly?: boolean;
  stageWrapperProps?: SharedShellProps;
  onStagesChange?: (flowId: string, stages: OtherTransactionsDisplayStage[]) => void;
}

/** Production and local walkthroughs use exactly the same workflow shell. */
export default function OtherTransactionsWorkflow({
  flowId, workflowName, currentStage, onStageChange, onBack,
  readOnly = false, stageWrapperProps, onStagesChange,
}: Props) {
  const navigationGuard = useRef<() => Promise<boolean>>(async()=>true);
  const selectStage = async (index: number) => { if (await navigationGuard.current()) onStageChange(index); };
  const leaveWorkflow = async () => { if (await navigationGuard.current()) onBack(); };
  const [workspace, setWorkspace] = useState<api.OtherTransactionsWorkspace | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { let alive=true; api.getOtherTransactionsWorkspace(flowId).then(response => {if (!response.success || !response.data) throw new Error(response.error || 'Could not load saved workflow.');if(alive)setWorkspace(response.data);}).catch(error => {if(alive)setError(toUserFacingError(error));});return()=>{alive=false;};},[flowId]);
  const stages = useMemo(() => workspace?.flow_id === flowId
    ? getOtherTransactionsStages(workspace.entry_origin).map((stage, i) => ({
      ...stage,
      ...(workspace.route?.[i] ? {
        name: workspace.route[i].name,
        description: workspace.route[i].purpose,
        purpose: workspace.route[i].purpose,
      } : {}),
    }))
    : [], [flowId, workspace]);
  useEffect(() => {
    if (stages.length) {
      // Companion grounding and feedback share the visible route labels only.
      onStagesChange?.(flowId, stages.map(({name, description}) => ({name, description})));
    }
  }, [flowId, onStagesChange, stages]);
  if(error)return <Alert severity="error">{error}</Alert>;
  if(!workspace || workspace.flow_id !== flowId)return <Box sx={{display:'flex',justifyContent:'center',p:4}}><CircularProgress aria-label="Loading saved workflow"/></Box>;
  return (
    <StageWrapper
      {...stageWrapperProps}
      config={stages[currentStage] || stages[0]}
      currentStage={currentStage}
      totalStages={stages.length}
      stages={stages.map(stage => stage.name)}
      workflowName={workflowName}
      workflowTypeName="Other Transactions"
      workflowId={flowId}
      workflowType="other_transactions"
      isViewOnly={readOnly}
      fullWidth
      onBack={()=>void leaveWorkflow()}
      onStageSelect={index=>void selectStage(index)}
    >
      <OtherTransactionsWorkspace
        flowId={flowId}
        stageIndex={currentStage}
        onStageChange={onStageChange}
        readOnly={readOnly}
        initialWorkspace={workspace}
        onWorkspaceChange={setWorkspace}
        registerNavigationGuard={guard=>{navigationGuard.current=guard;}}
      />
    </StageWrapper>
  );
}
